/**
 * Cell controls — inline редакторы для ячеек таблиц в стиле Linear.
 *
 *  • CellTextField — текстовое поле: показывает значение, при клике превращается в Input.
 *  • CellSelect    — компактный селектор. Выглядит как текст, без шеврона.
 *  • CellPicker    — универсальный popover-контрол с кастомным содержимым
 *                    (используется для персон: триггер-ячейка + поиск внутри).
 *
 * Стиль: text-[12.5px] leading-none font-normal, padding как у ячейки,
 * скругление rounded, hover bg-muted/70, без рамок и иконок раскрытия.
 */
import { type ReactNode, useEffect, useRef, useState } from "react"
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Input } from "@/components/ui/input"
import { HugeiconsIcon } from "@hugeicons/react"
import { Tick02Icon } from "@hugeicons/core-free-icons"
import { cn } from "@/lib/utils"

// ─── CellTextField ──────────────────────────────────────────────────────────
//
// Inline-редактирование текстового значения.
//   • В покое выглядит как обычная ячейка: текст или пусто.
//   • По клику переключается в input.
//   • По blur или Enter — коммитит значение. Esc — откатывает.
//
// Это нужно, чтобы текстовые поля визуально не отличались от остальных
// контролов (которые показывают только текст до клика).

type CellTextFieldProps = {
  value: string | null
  onCommit: (next: string | null) => void
  type?: "text" | "number" | "datetime-local" | "email" | "tel" | "url"
  placeholder?: string
  className?: string
  /** Convert raw input string into the value that gets committed */
  parse?: (raw: string) => string | null
  /** Convert committed value into raw string for the input */
  format?: (value: string | null) => string
  /** Treat empty string as null when committing */
  clearOnEmpty?: boolean
  /**
   * Trigger-button style:
   *   • "cell" — кнопка h-6, по клику превращается в input (по умолчанию).
   *   • "chip" — pill-чип h-7 rounded-full border, input рендерится сразу
   *              (для create-форм).
   */
  triggerVariant?: "cell" | "chip"
  /** Иконка слева (только в chip-варианте). */
  leadingIcon?: ReactNode
}

export function CellTextField({
  value,
  onCommit,
  type = "text",
  placeholder,
  className,
  parse,
  format,
  clearOnEmpty = true,
  triggerVariant = "cell",
  leadingIcon,
}: CellTextFieldProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  const hasValue = value !== null && value !== undefined && value !== ""
  const isEmpty = !hasValue
  const isChip = triggerVariant === "chip"

  // Sync draft from external value when not editing
  useEffect(() => {
    if (!editing) {
      setDraft(format ? format(value) : value ?? "")
    }
  }, [value, editing, format])

  // Auto-focus + select when entering edit mode
  useEffect(() => {
    if (editing) {
      const el = inputRef.current
      if (el) {
        el.focus()
        if (type !== "datetime-local") el.select()
      }
    }
  }, [editing, type])

  function commit() {
    const next = parse ? parse(draft) : draft
    if (clearOnEmpty && (next === "" || next === null)) {
      onCommit(null)
    } else {
      onCommit(next ?? "")
    }
    setEditing(false)
  }

  function cancel() {
    setDraft(format ? format(value) : value ?? "")
    setEditing(false)
  }

  // Chip-вариант: сразу показываем pill-input (без промежуточной кнопки).
  if (isChip) {
    return (
      <div
        className={cn(
          "group/chip inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-border/80 bg-background px-2.5 text-xs leading-none font-normal text-foreground/80 transition-colors hover:bg-muted/60 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30",
          isEmpty && "text-muted-foreground/80",
          className,
        )}
      >
        {leadingIcon ? (
          <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
            {leadingIcon}
          </span>
        ) : null}
        <Input
          ref={inputRef}
          type={type}
          value={draft || (hasValue ? (value ?? "") : "")}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              commit()
            } else if (e.key === "Escape") {
              e.preventDefault()
              cancel()
            }
          }}
          className="h-full min-w-0 flex-1 border-0 bg-transparent px-0 text-inherit shadow-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
    )
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type={type}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            commit()
          } else if (e.key === "Escape") {
            e.preventDefault()
            cancel()
          }
        }}
        className={cn(
          "h-6 w-full rounded border-0 bg-muted/70 px-1.5 text-[12.5px] font-normal leading-none shadow-none focus-visible:ring-0",
          className,
        )}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          setEditing(true)
        }
      }}
      className={cn(
        "flex h-6 w-full items-center rounded px-1.5 text-left text-[12.5px] leading-none font-normal transition-colors",
        "hover:bg-muted/70 focus:bg-muted/70 focus:outline-none",
        isEmpty && "text-muted-foreground/40",
        className,
      )}
    >
      <span className="truncate">{hasValue ? value : ""}</span>
    </button>
  )
}

// ─── CellSelect ─────────────────────────────────────────────────────────────

export type CellSelectOption = {
  value: string
  label: ReactNode
  color?: string
  disabled?: boolean
}

type CellSelectProps = {
  value: string | null
  options: CellSelectOption[]
  onChange: (next: string | null) => void
  /** Resolved label/JSX for the current value (shown when collapsed) */
  renderValue?: (option: CellSelectOption | undefined) => ReactNode
  /** Show a "—" entry that clears the value */
  clearable?: boolean
  /**
   * Rendering style for the option list:
   *   • "check"  — checkbox + label (good for multi-select intent)
   *   • "plain"  — clickable row, no checkbox (good for single-select like status)
   * Default: "check"
   */
  itemVariant?: "check" | "plain"
  /** Placeholder shown in the popover search input */
  searchPlaceholder?: string
  className?: string
  /**
   * Trigger-button style:
   *   • "cell" — узкая h-6 ячейка (по умолчанию; для таблиц и rail).
   *   • "chip" — pill-чип h-7 rounded-full border (для create-форм).
   *
   * В chip-варианте `leadingIcon` рисуется слева, `placeholder` показывается
   * когда значение пустое.
   */
  triggerVariant?: "cell" | "chip"
  /** Иконка слева от label (только в chip-варианте). */
  leadingIcon?: ReactNode
  /** Текст-плейсхолдер, когда значение пустое (только в chip-варианте). */
  placeholder?: string
}

export function CellSelect({
  value,
  options,
  onChange,
  renderValue,
  clearable = false,
  itemVariant = "check",
  searchPlaceholder = "Поиск...",
  className,
  triggerVariant = "cell",
  leadingIcon,
  placeholder,
}: CellSelectProps) {
  const [open, setOpen] = useState(false)
  const current = options.find((opt) => opt.value === value)
  const hasValue = current !== undefined && value !== null
  const isChip = triggerVariant === "chip"

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={cn(
            isChip
              ? "group/chip inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-border/80 bg-background px-2.5 text-left text-xs leading-none font-normal text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 data-[open=true]:bg-muted/60 data-[open=true]:text-foreground"
              : "flex h-6 w-full items-center gap-1.5 rounded px-1.5 text-left text-[12.5px] leading-none font-normal transition-colors hover:bg-muted/70 focus:bg-muted/70 focus:outline-none data-[open=true]:bg-muted/70",
            !hasValue && !isChip && "text-muted-foreground/40",
            !hasValue && isChip && "text-muted-foreground/80",
            className,
          )}
          data-open={open}
        >
          {isChip && leadingIcon ? (
            <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
              {leadingIcon}
            </span>
          ) : null}
          <span className="truncate flex-1">
            {hasValue
              ? (renderValue ? renderValue(current) : current!.label)
              : (isChip ? (placeholder ?? "") : "")}
          </span>
        </button>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-56 p-0">
        <CellSelectList
          value={value}
          options={options}
          onChange={(next) => {
            onChange(next)
            setOpen(false)
          }}
          clearable={clearable}
          itemVariant={itemVariant}
          searchPlaceholder={searchPlaceholder}
        />
      </PopoverContent>
    </Popover>
  )
}

// ─── Reusable list (used by CellSelect + CellPicker) ───────────────────────

type CellSelectListProps = {
  value: string | null
  options: CellSelectOption[]
  onChange: (next: string | null) => void
  clearable?: boolean
  itemVariant?: "check" | "plain"
  searchPlaceholder?: string
  /** When true, hides the search input (used by CellPicker which provides its own) */
  hideSearch?: boolean
}

export function CellSelectList({
  value,
  options,
  onChange,
  clearable = false,
  itemVariant = "check",
  searchPlaceholder = "Поиск...",
  hideSearch = false,
}: CellSelectListProps) {
  const [query, setQuery] = useState("")
  const q = query.trim().toLowerCase()
  const filtered = q
    ? options.filter((opt) => extractText(opt.label).toLowerCase().includes(q))
    : options

  return (
    <div>
      {!hideSearch && (
        <div className="border-b p-1">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-7 px-2 text-[12px] font-normal border-input/60 focus-visible:border-input focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
        </div>
      )}
      <ScrollArea className="max-h-64">
        <div className="p-1 space-y-0.5">
          {clearable && (
            <CellSelectRow
              checked={value === null || value === undefined}
              onSelect={() => onChange(null)}
              label="—"
              itemVariant={itemVariant}
              muted
            />
          )}
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-[12.5px] text-muted-foreground">
              Ничего не найдено
            </div>
          ) : (
            filtered.map((opt) => (
              <CellSelectRow
                key={opt.value}
                checked={value === opt.value}
                disabled={opt.disabled}
                color={opt.color}
                onSelect={() => onChange(opt.value)}
                label={opt.label}
                itemVariant={itemVariant}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

function CellSelectRow({
  checked,
  onSelect,
  label,
  color,
  muted,
  disabled,
  itemVariant,
}: {
  checked: boolean
  onSelect: () => void
  label: ReactNode
  color?: string
  muted?: boolean
  disabled?: boolean
  itemVariant: "check" | "plain"
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault()
        onSelect()
      }}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] leading-none font-normal transition-colors hover:bg-accent focus:bg-accent focus:outline-none",
        checked && itemVariant === "check" && "bg-accent/50",
        !checked && (muted ? "text-muted-foreground" : "text-foreground/85"),
        disabled && "opacity-50 pointer-events-none",
      )}
    >
      {itemVariant === "check" ? (
        <span
          className={cn(
            "size-3.5 rounded-[3px] border flex items-center justify-center shrink-0",
            checked ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/30",
          )}
        >
          {checked && <HugeiconsIcon icon={Tick02Icon} strokeWidth={3} className="size-2.5 text-white" />}
        </span>
      ) : null}
      {color && (
        <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      )}
      <span className="truncate flex-1">{label}</span>
    </button>
  )
}

// ─── CellPicker ─────────────────────────────────────────────────────────────
//
// Trigger-cell (looks like a regular cell) + popover with custom content.
// Use when you need a search/filter inside the popover (e.g. PersonCombobox).

type CellPickerProps = {
  /** What to render when the popover is closed. Required. */
  trigger: ReactNode
  /** Content rendered inside the popover (search + list, etc.) */
  children: (api: { close: () => void }) => ReactNode
  /** Optional className for the trigger button (matches the cell) */
  className?: string
  /**
   * Trigger-button style:
   *   • "cell" — узкая h-6 ячейка (по умолчанию; для таблиц и rail).
   *   • "chip" — pill-чип h-7 rounded-full border (для create-форм).
   */
  triggerVariant?: "cell" | "chip"
}

export function CellPicker({ trigger, children, className, triggerVariant = "cell" }: CellPickerProps) {
  const [open, setOpen] = useState(false)
  const isChip = triggerVariant === "chip"
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={cn(
            isChip
              ? "group/chip inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-border/80 bg-background px-2.5 text-left text-xs leading-none font-normal text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 data-[open=true]:bg-muted/60 data-[open=true]:text-foreground"
              : "flex h-6 w-full items-center gap-1.5 rounded px-1.5 text-left text-[12.5px] leading-none font-normal transition-colors hover:bg-muted/70 focus:bg-muted/70 focus:outline-none data-[open=true]:bg-muted/70",
            className,
          )}
          data-open={open}
        >
          {trigger}
        </button>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-72 p-0">
        {children({ close: () => setOpen(false) })}
      </PopoverContent>
    </Popover>
  )
}

// ─── helpers ────────────────────────────────────────────────────────────────

export function extractText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(extractText).join("")
  if (node && typeof node === "object" && "props" in node) {
    return extractText((node as any).props.children)
  }
  return ""
}